import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react'
import { useAuth } from './AuthContext'
import { useOrganization } from './OrganizationContext'
import { rbacAPI } from '@/lib/api'

interface PermissionsContextType {
    hasPermission: (permission: string) => boolean
    hasRole: (role: string | string[]) => boolean
    isSuperAdmin: () => boolean
    isAdmin: () => boolean
    isOrgAdmin: () => boolean
    userRole?: string
    userPermissions: string[]
    loading: boolean
    refreshPermissions: () => Promise<void>
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined)

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
    const { user, activeRole } = useAuth()
    const { activeOrgId } = useOrganization()
    const [userPermissions, setUserPermissions] = useState<Set<string>>(new Set())
    const [loading, setLoading] = useState(true)
    const hasFetchedRef = useRef(false)
    const lastUserIdRef = useRef<string | null>(null)
    const lastOrgIdRef = useRef<string | null>(null)

    const fetchUserPermissions = useCallback(async (force: boolean = false) => {
        if (!user) {
            setUserPermissions(new Set())
            setLoading(false)
            hasFetchedRef.current = false
            lastUserIdRef.current = null
            lastOrgIdRef.current = null
            return
        }

        const orgScope = activeOrgId ?? null

        // Refetch when user or active org changes, unless forced refresh already requested.
        if (
            !force &&
            hasFetchedRef.current &&
            lastUserIdRef.current === user.id &&
            lastOrgIdRef.current === orgScope
        ) {
            return
        }

        try {
            setLoading(true)
            const response = await rbacAPI.getUserPermissions(
                user.id,
                activeOrgId ?? undefined,
            )
            setUserPermissions(new Set(response.permissions || []))
            hasFetchedRef.current = true
            lastUserIdRef.current = user.id
            lastOrgIdRef.current = orgScope
        } catch (error) {
            console.error('Failed to fetch user permissions:', error)
            // Fail closed: do not grant inferred permissions when RBAC API is unavailable.
            setUserPermissions(new Set())
            hasFetchedRef.current = true
            lastUserIdRef.current = user.id
            lastOrgIdRef.current = orgScope
        } finally {
            setLoading(false)
        }
    }, [user, activeOrgId])

    useEffect(() => {
        fetchUserPermissions()
    }, [fetchUserPermissions])

    const hasPermission = (permission: string): boolean => {
        if (!user) return false
        // Admin/Super Admin has all permissions
        if (activeRole === 'admin' || activeRole === 'super_admin') return true

        // Check implicit permissions
        const implicitPermissions = [
            'user:read_own',
            'user:update_own'
        ]
        if (implicitPermissions.includes(permission)) {
            return true // All authenticated users can manage their own profile
        }
        return userPermissions.has(permission)
    }

    const hasRole = (role: string | string[]): boolean => {
        if (!user || !activeRole) return false
        if (activeRole === 'admin' || activeRole === 'super_admin') return true
        if (Array.isArray(role)) {
            return role.includes(activeRole)
        }
        return activeRole === role
    }

    const isSuperAdmin = (): boolean => !!user && (activeRole === 'admin' || activeRole === 'super_admin')
    const isAdmin = (): boolean => !!user && (activeRole === 'admin' || activeRole === 'super_admin')
    const isOrgAdmin = (): boolean => !!user && (
        activeRole === 'org_admin' ||
        activeRole === 'admin' ||
        activeRole === 'super_admin'
    )

    const refreshPermissions = async () => {
        await fetchUserPermissions(true)
    }

    const value = {
        hasPermission,
        hasRole,
        isSuperAdmin,
        isAdmin,
        isOrgAdmin,
        userRole: activeRole || user?.role,
        userPermissions: Array.from(userPermissions),
        loading,
        refreshPermissions
    }

    return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export function usePermissionsContext() {
    const context = useContext(PermissionsContext)
    if (context === undefined) {
        throw new Error('usePermissionsContext must be used within a PermissionsProvider')
    }
    return context
}
